import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";

/**
 * This controller handles the interaction between the editor and the assistant.
 *
 * It manages:
 * 1. Receiving text from the editor (via editor:request-assistant event)
 * 2. Checking if there's an active conversation to continue or if a new one should be created
 * 3. Adding text to existing conversations or navigating to create new ones
 * 4. Setting up the conversation form with the received text
 *
 * Flow:
 * - Editor dispatches 'editor:request-assistant' with text
 * - Controller checks for existing active conversation
 * - If active conversation exists: adds text to existing conversation
 * - If no active conversation: activates assistant tab and navigates to new conversation
 * - On frame load, controller:
 *   - Selects the appropriate conversation type
 *   - Sets up the form with the received text
 */

export default class extends Controller {
  static targets = ["menu", "menuItem"]
  static values = {
    newConversationPath: { type: String, default: '/asistente/new?context=tools' },
    writeTemplate: { type: String, default: 'Redacta: {{text}}' }
  }

  // Helper method to check if there's an active conversation
  _hasActiveConversation() {
    const conversationFrame = document.getElementById('conversation_frame')
    if (!conversationFrame) return false

    // Check if there are existing messages in the conversation
    const messagesContainer = conversationFrame.querySelector('#messages, [data-messages-list-target="messages"]')
    if (messagesContainer) {
      const messages = messagesContainer.querySelectorAll('[data-role="message"], .assistant--message, .message')
      if (messages.length > 0) return true
    }

    // Check if there's a conversation header indicating an active conversation
    const conversationHeader = conversationFrame.querySelector('#conversation-context, [id^="conversation-"], .conversation-header')
    if (conversationHeader) return true

    // Check if there's a message input that suggests an active conversation
    const messageForm = conversationFrame.querySelector('form[action*="/messages"], form[data-turbo-frame="conversation_frame"]')
    if (messageForm) {
      // Look for hidden inputs that indicate an existing conversation
      const conversationInput = messageForm.querySelector('input[name*="conversation"]')
      if (conversationInput && conversationInput.value) return true
    }

    return false
  }

  // Helper method to add text to existing conversation
  _addToExistingConversation(text) {
    const conversationFrame = document.getElementById('conversation_frame')
    if (!conversationFrame) return false

    // Find the input field in the conversation
    const inputField = conversationFrame.querySelector('textarea[name*="question"], input[name*="question"], textarea[data-conversation-menu-target="input"]')
    if (inputField) {
      // If input is empty, set the text directly
      if (!inputField.value.trim()) {
        inputField.value = text
      } else {
        // If input has content, append with a newline
        inputField.value = inputField.value.trim() + '\n\n' + text
      }

      // Focus the input and dispatch events to trigger autogrow and other listeners
      inputField.focus()
      inputField.dispatchEvent(new Event('input', { bubbles: true }))
      inputField.dispatchEvent(new Event('change', { bubbles: true }))

      return true
    }

    return false
  }

  connect() {
    this._bindEventHandlers();
  }

  disconnect() {
    this._removeEventHandlers();
  }

  handleEditorRequest(event) {
    const { text, tab, eventId } = event.detail;
    const userQuestion = this.writeTemplateValue.replace('{{text}}', text);

    // Check if there's already an active conversation
    if (this._hasActiveConversation()) {
      if (this._addToExistingConversation(userQuestion)) {
        // Make sure the assistant tab is active
        if (tab) {
          this.activateTab(tab);
        }
        return;
      }
    }

    // No active conversation or couldn't add to existing, create new conversation
    this._storePendingState(userQuestion, tab, eventId);

    if (this.pendingTab) {
      this.activateTab(this.pendingTab);
    }

    this._handleNavigation();
  }

  frameLoaded() {
    if (!this.pendingText) return;
    this._setInputValueDirectly(this.pendingText);
    this._clearPendingState();
  }

  _storePendingState(text, tab, eventId) {
    this.pendingText = text;
    this.pendingTab = tab;
    this.pendingEventId = eventId;
  }

  _clearPendingState() {
    this.pendingText = null;
    this.pendingEventId = null;
  }

  _handleNavigation() {
    const currentPath = window.location.pathname;

    if (currentPath === '/asistente/new') {
      this.frameLoaded();
    } else {
      this._navigateToNewConversation();
    }
  }

  _navigateToNewConversation() {
    const url = new URL(this.newConversationPathValue, window.location.origin);

    // If we are in the Writer, ensure that writer_document_id is in the URL
    const conversationFrame = document.getElementById('conversation_frame')
    if (conversationFrame) {
      const writerDocumentId = conversationFrame.dataset.writerDocumentId
      if (writerDocumentId) {
        // Add writer_document_id to the prompt_params if it is not present
        if (!url.searchParams.has('prompt_params[writer_document_id]')) {
          url.searchParams.set('prompt_params[writer_document_id]', writerDocumentId)
        }
      }
    }

    visit(url.toString(), { frame: 'conversation_frame' });
  }

  _bindEventHandlers() {
    this.handleEditorRequest = this.handleEditorRequest.bind(this);
    this.frameLoaded = this.frameLoaded.bind(this);
    window.addEventListener('editor:request-assistant', this.handleEditorRequest);
    window.addEventListener('turbo:frame-load', this.frameLoaded);
  }

  _removeEventHandlers() {
    window.removeEventListener('editor:request-assistant', this.handleEditorRequest);
    window.removeEventListener('turbo:frame-load', this.frameLoaded);
  }

  activateTab(tabSpec, attempts = 0, maxAttempts = 10) {
    if (!tabSpec || attempts >= maxAttempts) return;

    const [tabsId, tabId] = tabSpec.split('#');
    if (!tabsId || !tabId) return;

    const tabsElement = document.getElementById(tabsId);
    if (!tabsElement) {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
      return;
    }

    const tabsController = this.application.getControllerForElementAndIdentifier(tabsElement, 'tabs');
    if (!tabsController) {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
      return;
    }

    const tabItem = tabsController.tabItemTargets.find(item =>
      item.dataset.tabsControlsParam === tabId
    );

    if (tabItem) {
      setTimeout(() => tabItem.click(), 50);
    } else {
      setTimeout(() => this.activateTab(tabSpec, attempts + 1, maxAttempts), 100);
    }
  }


  _setInputValueDirectly(text) {
    if (!text) return;

    const conversationFrame = document.getElementById('conversation_frame');
    if (!conversationFrame) return;

    // Try multiple approaches to find the input

    // 1. Look for the input with data-conversation-menu-target="input"
    const inputElement = conversationFrame.querySelector('[data-conversation-menu-target="input"]');
    if (inputElement) {
      this._setAndDispatchEvents(inputElement, text);
      return;
    }

    // 2. Try to find by id
    const messageQuestion = conversationFrame.querySelector('#message_question');
    if (messageQuestion) {
      this._setAndDispatchEvents(messageQuestion, text);
      return;
    }

    // 3. Try to find any text input in the frame
    const textInputs = conversationFrame.querySelectorAll('input[type="text"]');
    if (textInputs.length > 0) {
      this._setAndDispatchEvents(textInputs[0], text);
      return;
    }

    // 4. Try to find any textarea in the frame
    const textareas = conversationFrame.querySelectorAll('textarea');
    if (textareas.length > 0) {
      this._setAndDispatchEvents(textareas[0], text);
    }
  }

  _setAndDispatchEvents(element, text) {
    element.value = text;
    element.focus();
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
